import { useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePostHog } from 'posthog-js/react';
import { getCookie } from '../services/analytics';
import { isDuplicateEvent } from '../utils/eventDeduplication';
import { deduplicatedPostHogCall } from '../utils/posthogDeduplication';

// Declare dataLayer for GTM
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/**
 * Generate unique event ID
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * React hook for tracking analytics events
 * Captures all events except add_payment_info (pageviews are manually tracked)
 * Sends events to both PostHog and Google Tag Manager (GTM)
 * 
 * @example
 * ```tsx
 * const { track } = useTracking();
 * track('signup', { email: user.email, method: 'email', external_id: user.id, user_id: user.id });
 * track('pageview', { page_path: '/home', page_title: 'Home' });
 * ```
 */
export function useTracking() {
  const { user } = useAuth();
  const posthog = usePostHog();
  
  // Use ref to store track function to avoid dependency issues
  const posthogRef = useRef(posthog);
  const userRef = useRef(user);
  
  // Update refs when values change
  posthogRef.current = posthog;
  userRef.current = user;

  const track = useCallback(
    (eventName: string, properties: Record<string, any> = {}) => {
      // Skip add_payment_info events - we don't want to track these
      if (eventName === 'add_payment_info') {
        return;
      }

      // Build event data with user information FIRST (before deduplication check)
      const eventData: Record<string, any> = {
        ...properties,
        // Always include user identifiers if available
        external_id: userRef.current?.id || properties.external_id || '',
        user_id: userRef.current?.id || properties.user_id || '',
      };

      // Add email if user is authenticated OR if email is in properties
      // Priority: properties.email > user.email
      if (properties.email) {
        eventData.email = properties.email;
      } else if (userRef.current?.email) {
        eventData.email = userRef.current.email;
      }

      // Store email before cleanup (for GTM protection)
      const emailValue = eventData.email;

      // Remove empty values to keep payload clean (but preserve email if present)
      Object.keys(eventData).forEach(key => {
        // Don't remove email if it exists (email is critical for user identification)
        if (key === 'email' && eventData.email) {
          return; // Keep email
        }
        if (eventData[key] === undefined || eventData[key] === null || eventData[key] === '') {
          delete eventData[key];
        }
      });

      // Check for duplicate events AFTER merging user data (so hash is accurate)
      if (isDuplicateEvent(eventName, eventData)) {
        return; // Event already tracked recently, skip
      }
      
      // Generate unique event ID if not provided (used for both GTM and PostHog)
      if (!eventData.event_id) {
        eventData.event_id = generateEventId();
      }
      
      // Get Facebook cookies (for both GTM and PostHog)
      const fbp = typeof window !== 'undefined' ? (getCookie('_fbp') || '') : '';
      const fbc = typeof window !== 'undefined' ? (getCookie('_fbc') || '') : '';
      
      // Add Facebook cookies to eventData (for PostHog)
      // fbp should always be included (even if empty), fbc only if present
      eventData.fbp = fbp;
      if (fbc) {
        eventData.fbc = fbc;
      }
      
      // Track if PostHog event was sent (to prevent retry duplicates)
      const posthogEventSent = { value: false };

      // Send to Google Tag Manager (GTM) - always send, even if PostHog isn't ready
      if (typeof window !== 'undefined') {
        // Initialize dataLayer if needed
        window.dataLayer = window.dataLayer || [];
        
        // Get user ID from event data
        const userId = eventData.user_id || eventData.external_id || '';
        const externalId = eventData.external_id || userId || '';
        
        // Build GTM payload
        const gtmPayload: Record<string, any> = {
          event: eventName,
          event_id: eventData.event_id,
          external_id: externalId,
          fbp: fbp,
          ...eventData, // Include all event-specific data (including fbp and fbc)
        };
        
        // Add user_id if available
        if (userId) {
          gtmPayload.user_id = userId;
        }
        
        // Remove empty values (except fbp, event, and email which should always be included if present)
        // Email is critical for user identification and should not be removed
        Object.keys(gtmPayload).forEach(key => {
          // Protect fbp, event, and email from deletion
          if (key === 'fbp' || key === 'event' || key === 'email') {
            return; // Keep these fields
          }
          if (gtmPayload[key] === undefined || gtmPayload[key] === null || gtmPayload[key] === '') {
            delete gtmPayload[key];
          }
        });
        
        // Ensure email is included if it was in the original eventData
        if (emailValue && !gtmPayload.email) {
          gtmPayload.email = emailValue;
        }
        
        // Push to dataLayer (GTM)
        window.dataLayer.push(gtmPayload);
      }

      // Send to PostHog - with retry logic if not ready
      const sendToPostHog = () => {
        // Check if already sent (prevent retry duplicates)
        if (posthogEventSent.value) {
          return true;
        }
        
        // Try to get PostHog instance from hook first, then from window as fallback
        const posthogInstance = posthogRef.current || (typeof window !== 'undefined' ? (window as any).posthog : null);
        
        if (posthogInstance && typeof posthogInstance.capture === 'function') {
          // Use deduplicated call to prevent duplicates (additional layer of protection)
          // eventData now includes fbp and fbc
          deduplicatedPostHogCall(posthogInstance, 'capture', eventName, eventData);
          posthogEventSent.value = true; // Mark as sent
          return true;
        }
        return false;
      };

      if (!sendToPostHog()) {
        // PostHog not ready yet - try to queue the event with retry mechanism
        console.warn('⚠️ [PostHog] PostHog not initialized, attempting to queue event:', eventName);
        
        // Retry mechanism: try again after short delays
        // This handles cases where PostHog is still initializing
        const maxRetries = 10;
        let retryCount = 0;
        
        const tryCapture = () => {
          // Check if already sent before retrying
          if (posthogEventSent.value) {
            return;
          }
          
          if (sendToPostHog()) {
            // Success after retry
          } else if (retryCount < maxRetries) {
            retryCount++;
            // Exponential backoff: 50ms, 100ms, 150ms, 200ms, etc. up to 500ms
            const delay = Math.min(50 * retryCount, 500);
            setTimeout(tryCapture, delay);
          } else {
            console.error('❌ [PostHog] Failed to capture event after', maxRetries, 'retries:', eventName);
          }
        };
        
        // Start retry after a short initial delay
        setTimeout(tryCapture, 50);
      }
    },
    [] // Empty dependency array - use refs instead
  );

  return { track };
}